/**
 * CommandPalette Component
 * Keyboard-driven command palette (Cmd+K / Ctrl+K)
 * Phase 2: Navigation - UI Architecture Restructure
 * Phase 4: Polish - Animations, responsive, accessibility
 *
 * Design tokens used (from data.yaml):
 * - Animation: 200ms (normal) for fade+scale
 * - Touch targets: min 44px for mobile
 * - Shadows: shadow-xl for modals
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  House,
  LogOut,
  Clock,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
  FileText,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useAnimatedOverlay } from './useAnimatedOverlay'

// UI copy
const translations = {
  searchPlaceholder: 'Search pages or commands…',
  noResults: 'No results',
  results: 'Results',
  pages: 'Pages',
  recent: 'Recent',
  toNavigate: 'to navigate',
  toSelect: 'to select',
  toClose: 'to close',
}

// Types
interface CommandItem {
  id: string
  label: string
  labelEn: string
  icon: typeof House
  type: 'page' | 'action'
  path?: string
  action?: () => void
  keywords?: string[]
}

// Storage key for recent items
const RECENT_ITEMS_KEY = 'ifcg-command-palette-recent'
const MAX_RECENT_ITEMS = 5

interface CommandPaletteProps {
  /** Whether the palette is open */
  isOpen: boolean
  /** Callback when palette should close */
  onClose: () => void
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentItems, setRecentItems] = useState<string[]>([])
  const { mounted: isVisible, animateIn: isAnimating } = useAnimatedOverlay(isOpen, 200)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)
  const prevMounted = useRef(false)

  // Define all command items
  const allItems: CommandItem[] = useMemo(
    () => [
      // Pages
      {
        id: 'advisor-dashboard',
        label: 'Dashboard',
        labelEn: 'Advisor Dashboard',
        icon: House,
        type: 'page',
        path: '/advisor',
        keywords: ['home', 'main', 'dashboard', 'advisor'],
      },
      {
        id: 'advisor-cases',
        label: 'Cases',
        labelEn: 'Advisor Cases',
        icon: FileText,
        type: 'page',
        path: '/advisor/cases',
        keywords: ['advisor', 'cases', 'proposal', 'quote', 'quotation'],
      },
      // Policies + Commission hidden until real data is ready
      // Admin Pages removed - Not for Advisor Portal
      // Actions
      {
        id: 'logout',
        label: 'Logout',
        labelEn: 'Sign out of Triple A',
        icon: LogOut,
        type: 'action',
        action: () => {
          logout()
        },
        keywords: ['signout', 'exit', 'logout', 'ออกจากระบบ'],
      },
    ],
    [logout]
  )

  // Load recent items from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_ITEMS_KEY)
      if (stored) {
        setRecentItems(JSON.parse(stored))
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [])

  // Save recent item
  const saveRecentItem = useCallback((itemId: string) => {
    setRecentItems((prev) => {
      const updated = [itemId, ...prev.filter((id) => id !== itemId)].slice(
        0,
        MAX_RECENT_ITEMS
      )
      try {
        localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated))
      } catch {
        // Ignore localStorage errors
      }
      return updated
    })
  }, [])

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      // Show recent items when no search query
      const recent = recentItems
        .map((id) => allItems.find((item) => item.id === id))
        .filter(Boolean) as CommandItem[]

      // Add remaining items not in recent
      const remaining = allItems.filter(
        (item) => !recentItems.includes(item.id)
      )

      return { recent, all: remaining }
    }

    const query = searchQuery.toLowerCase()
    const matched = allItems.filter((item) => {
      const matchLabel = item.label.toLowerCase().includes(query)
      const matchLabelEn = item.labelEn.toLowerCase().includes(query)
      const matchKeywords = item.keywords?.some((kw) => kw.includes(query))
      return matchLabel || matchLabelEn || matchKeywords
    })

    return { recent: [], all: matched }
  }, [searchQuery, allItems, recentItems])

  // Flat list for keyboard navigation
  const flatItems = useMemo(() => {
    return [...filteredItems.recent, ...filteredItems.all]
  }, [filteredItems])

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0)
  }, [searchQuery])

  // Handle item selection
  const handleSelect = useCallback(
    (item: CommandItem) => {
      saveRecentItem(item.id)
      onClose()

      if (item.action) {
        item.action()
      } else if (item.path) {
        navigate(item.path)
      }
    },
    [navigate, onClose, saveRecentItem]
  )

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setSelectedIndex((prev) =>
            prev < flatItems.length - 1 ? prev + 1 : 0
          )
          break
        case 'ArrowUp':
          event.preventDefault()
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : flatItems.length - 1
          )
          break
        case 'Enter': {
          event.preventDefault()
          const selectedItem = flatItems[selectedIndex]
          if (selectedItem) {
            handleSelect(selectedItem)
          }
          break
        }
        case 'Escape':
          event.preventDefault()
          onClose()
          break
      }
    },
    [flatItems, selectedIndex, handleSelect, onClose]
  )

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return
    const selectedElement = listRef.current.querySelector(
      `[data-index="${selectedIndex}"]`
    )
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  // Focus capture + scroll-lock while the palette is open (animation cycle
  // is handled by useAnimatedOverlay).
  useEffect(() => {
    if (!isOpen) return
    // Store current active element for focus restoration
    previousActiveElement.current = document.activeElement as HTMLElement
    // Focus input after short delay for animation
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus()
    }, 50)
    document.body.style.overflow = 'hidden'
    return () => {
      clearTimeout(focusTimer)
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Reset search + selection + restore focus when the palette finishes closing
  // and leaves the DOM (mirrors the prior in-timer close callback).
  useEffect(() => {
    if (prevMounted.current && !isVisible) {
      setSearchQuery('')
      setSelectedIndex(0)
      previousActiveElement.current?.focus()
    }
    prevMounted.current = isVisible
  }, [isVisible])

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  // Don't render if not visible
  if (!isVisible) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] sm:pt-[15vh] px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="command-palette-title"
      onClick={handleBackdropClick}
    >
      {/* Backdrop - opacity controlled by isAnimating, click to close */}
      <div
        className={`
          absolute inset-0 bg-black/50 backdrop-blur-sm
          transition-opacity duration-200 ease-in-out
          motion-reduce:transition-none cursor-pointer
          ${isAnimating ? 'opacity-100' : 'opacity-0'}
        `}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Palette Container - scale and opacity controlled by isAnimating */}
      <div
        ref={containerRef}
        className={`
          relative w-full max-w-lg bg-background rounded-lg border-2 border-border shadow-xl overflow-hidden
          transition-[opacity,transform] duration-200 ease-out
          motion-reduce:transition-none motion-reduce:transform-none
          ${isAnimating
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 -translate-y-4'
          }
        `}
        onKeyDown={handleKeyDown}
      >
        {/* Hidden title for screen readers */}
        <h2 id="command-palette-title" className="sr-only">
          Command Palette
        </h2>

        {/* Search Input - 44px touch target */}
        <div className="flex items-center gap-3 border-b-2 border-border-subtle px-4 py-3 min-h-[52px]">
          <Search className="h-5 w-5 text-foreground-subtle flex-shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={translations.searchPlaceholder}
            className="flex-1 bg-transparent text-foreground placeholder:text-foreground-subtle outline-none text-base min-h-[44px]"
            aria-label={translations.searchPlaceholder}
            aria-controls="command-palette-results"
            aria-activedescendant={flatItems[selectedIndex] ? `command-item-${flatItems[selectedIndex].id}` : undefined}
          />
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs text-foreground-subtle bg-background-muted rounded border border-border-subtle">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div
          ref={listRef}
          id="command-palette-results"
          className="max-h-[50vh] sm:max-h-80 overflow-y-auto p-2"
          role="listbox"
          aria-label="Search results"
        >
          {flatItems.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-foreground-subtle">
              {translations.noResults}
            </p>
          ) : (
            <>
              {/* Recent Items Section */}
              {filteredItems.recent.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-foreground-subtle uppercase tracking-wide">
                    <Clock className="h-3 w-3" />
                    {translations.recent}
                  </div>
                  {filteredItems.recent.map((item, index) => (
                    <CommandItemComponent
                      key={item.id}
                      item={item}
                      index={index}
                      isSelected={selectedIndex === index}
                      onSelect={() => handleSelect(item)}
                      onHover={() => setSelectedIndex(index)}
                    />
                  ))}
                </div>
              )}

              {/* All Items / Search Results */}
              {filteredItems.all.length > 0 && (
                <div>
                  {filteredItems.recent.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-foreground-subtle uppercase tracking-wide">
                      {searchQuery ? translations.results : translations.pages}
                    </div>
                  )}
                  {filteredItems.all.map((item, index) => {
                    const actualIndex = filteredItems.recent.length + index
                    return (
                      <CommandItemComponent
                        key={item.id}
                        item={item}
                        index={actualIndex}
                        isSelected={selectedIndex === actualIndex}
                        onSelect={() => handleSelect(item)}
                        onHover={() => setSelectedIndex(actualIndex)}
                      />
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer with keyboard hints - hidden on very small screens */}
        <div className="hidden sm:flex items-center justify-between gap-4 border-t-2 border-border-subtle px-4 py-2 text-xs text-foreground-subtle bg-background-subtle">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <ArrowUp className="h-3 w-3" aria-hidden="true" />
              <ArrowDown className="h-3 w-3" aria-hidden="true" />
              <span>{translations.toNavigate}</span>
            </span>
            <span className="flex items-center gap-1">
              <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
              <span>{translations.toSelect}</span>
            </span>
          </div>
          <span className="flex items-center gap-1">
            <span>ESC</span> <span>{translations.toClose}</span>
          </span>
        </div>

        {/* Mobile footer - simplified */}
        <div className="flex sm:hidden items-center justify-center gap-4 border-t-2 border-border-subtle px-4 py-3 text-xs text-foreground-subtle bg-background-subtle">
          <span>Tap outside to close</span>
        </div>
      </div>
    </div>
  )
}

// Individual command item component
interface CommandItemComponentProps {
  item: CommandItem
  index: number
  isSelected: boolean
  onSelect: () => void
  onHover: () => void
}

function CommandItemComponent({
  item,
  index,
  isSelected,
  onSelect,
  onHover,
}: CommandItemComponentProps) {
  const Icon = item.icon

  return (
    <button
      type="button"
      id={`command-item-${item.id}`}
      role="option"
      aria-selected={isSelected}
      data-index={index}
      onClick={onSelect}
      onMouseEnter={onHover}
      onFocus={onHover}
      className={`
        w-full flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px]
        text-left transition-colors duration-150
        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset
        ${isSelected
          ? 'bg-primary text-white'
          : 'text-foreground hover:bg-background-muted'
        }
      `}
    >
      <Icon
        className={`h-5 w-5 flex-shrink-0 ${
          isSelected ? 'text-white' : 'text-foreground-muted'
        }`}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <span className="block font-medium truncate">{item.label}</span>
        <span
          className={`block text-xs truncate ${
            isSelected ? 'text-white/80' : 'text-foreground-subtle'
          }`}
        >
          {item.labelEn}
        </span>
      </div>
      {item.type === 'action' && (
        <span
          className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${
            isSelected
              ? 'bg-white/20 text-white'
              : 'bg-background-muted text-foreground-subtle'
          }`}
          aria-label="Action command"
        >
          Action
        </span>
      )}
    </button>
  )
}

export default CommandPalette
